import type {
  CalculatedColumn,
  CalculatedDimension,
  CalculatedMeasure,
  Dimension,
  DimensionRole,
  FormatDefinition,
  Hierarchy,
  HierarchyLevel,
  Kpi,
  LogicalDataType,
  Measure,
  SemanticColumn,
  SemanticModel,
  SemanticTable,
  SemanticType,
} from '@aibi/contracts';

export type ModelColumn = SemanticColumn | CalculatedColumn;
export type ModelDimension = Dimension | CalculatedDimension;
export type ModelMeasure = Measure | CalculatedMeasure;

export const isCalculatedColumn = (column: ModelColumn): column is CalculatedColumn =>
  'expression' in column;
export const isCalculatedDimension = (
  dimension: ModelDimension,
): dimension is CalculatedDimension => 'expression' in dimension;
export const isCalculatedMeasure = (measure: ModelMeasure): measure is CalculatedMeasure =>
  'expression' in measure;

export interface ResolvedColumn {
  readonly table: SemanticTable;
  readonly column: ModelColumn;
}

/** Dimension con su tabla y tipo efectivos, lista para planificar o validar. */
export interface ResolvedDimension {
  readonly dimension: ModelDimension;
  readonly table: SemanticTable;
  /** Columna de origen; `undefined` en dimensiones calculadas. */
  readonly column: ModelColumn | undefined;
  readonly role: DimensionRole;
  readonly dataType: LogicalDataType;
  readonly semanticType: SemanticType | undefined;
}

const TEMPORAL_TYPES: ReadonlySet<LogicalDataType> = new Set(['date', 'datetime']);

export const isTemporalDimension = (resolved: ResolvedDimension): boolean =>
  resolved.role === 'temporal' || TEMPORAL_TYPES.has(resolved.dataType);

export const isGeographicDimension = (resolved: ResolvedDimension): boolean =>
  resolved.role === 'geographic' ||
  resolved.dataType === 'geography' ||
  resolved.semanticType?.startsWith('geo.') === true;

/**
 * Indice de solo lectura para resolver referencias del modelo en O(1). Los ids son
 * unicos en todo el modelo, por lo que cada busqueda usa un unico identificador.
 * Lo consumen el Analysis Engine, el Query Engine y la validacion de acciones de IA.
 *
 * @example
 * const index = new SemanticModelIndex(model);
 * index.getMeasure('total_sales')?.name;
 */
export class SemanticModelIndex {
  readonly model: SemanticModel;
  readonly #tables = new Map<string, SemanticTable>();
  readonly #columns = new Map<string, ResolvedColumn>();
  readonly #dimensions = new Map<string, ModelDimension>();
  readonly #measures = new Map<string, ModelMeasure>();
  readonly #hierarchies = new Map<string, Hierarchy>();
  readonly #kpis = new Map<string, Kpi>();
  readonly #formats = new Map<string, FormatDefinition>();

  constructor(model: SemanticModel) {
    this.model = model;
    for (const table of model.tables) {
      this.#tables.set(table.id, table);
      for (const column of table.columns) this.#columns.set(column.id, { table, column });
    }
    for (const column of model.calculatedColumns) {
      const table = this.#tables.get(column.tableId);
      if (table) this.#columns.set(column.id, { table, column });
    }
    for (const dimension of [...model.dimensions, ...model.calculatedDimensions]) {
      this.#dimensions.set(dimension.id, dimension);
    }
    for (const measure of [...model.measures, ...model.calculatedMeasures]) {
      this.#measures.set(measure.id, measure);
    }
    for (const hierarchy of model.hierarchies) this.#hierarchies.set(hierarchy.id, hierarchy);
    for (const kpi of model.kpis) this.#kpis.set(kpi.id, kpi);
    for (const format of model.formats) this.#formats.set(format.id, format);
  }

  getTable(tableId: string): SemanticTable | undefined {
    return this.#tables.get(tableId);
  }

  /** Columna fisica o calculada. */
  getColumn(columnId: string): ResolvedColumn | undefined {
    return this.#columns.get(columnId);
  }

  /** Dimension base o calculada. */
  getDimension(dimensionId: string): ModelDimension | undefined {
    return this.#dimensions.get(dimensionId);
  }

  /** Medida base o calculada. */
  getMeasure(measureId: string): ModelMeasure | undefined {
    return this.#measures.get(measureId);
  }

  getHierarchy(hierarchyId: string): Hierarchy | undefined {
    return this.#hierarchies.get(hierarchyId);
  }

  getHierarchyLevel(hierarchyId: string, levelId: string): HierarchyLevel | undefined {
    return this.getHierarchy(hierarchyId)?.levels.find((level) => level.id === levelId);
  }

  getKpi(kpiId: string): Kpi | undefined {
    return this.#kpis.get(kpiId);
  }

  getFormat(formatId: string): FormatDefinition | undefined {
    return this.#formats.get(formatId);
  }

  /** Resuelve una dimension a su tabla y tipo efectivos; `undefined` si alguna referencia falta. */
  resolveDimension(dimensionId: string): ResolvedDimension | undefined {
    const dimension = this.getDimension(dimensionId);
    if (!dimension) return undefined;
    if (isCalculatedDimension(dimension)) {
      const table = this.getTable(dimension.tableId);
      return table
        ? {
            dimension,
            table,
            column: undefined,
            role: dimension.role,
            dataType: dimension.dataType,
            semanticType: undefined,
          }
        : undefined;
    }
    const resolved = this.getColumn(dimension.columnId);
    return resolved
      ? {
          dimension,
          table: resolved.table,
          column: resolved.column,
          role: dimension.role,
          dataType: resolved.column.dataType,
          semanticType: resolved.column.semanticType,
        }
      : undefined;
  }
}
